import type { ScopeRef } from "@enduring-systems/entitlements";

import { apiClient } from "@/lib/api-client";
import type { IdentityQuery, IdentitySuccess } from "@/lib/identity-contract";

type WorkspaceSearchResponse = IdentitySuccess<"searchWorkspaces">;

/** One workspace the platform-staff lookup returned. Personal Spaces are excluded. */
export type WorkspaceMatch = WorkspaceSearchResponse["data"][number];

/**
 * Find a workspace by name, as platform staff.
 *
 * The whole reason this exists is that an operator must never type a scope id:
 * they search a name, pick a row, and the id comes from the row (ENR-1943).
 * The route is audited server-side, so a search is a recorded disclosure and
 * not a free lookup.
 */
/**
 * `MAX_LIMIT` on the lookup route. Sent explicitly rather than left to the
 * route's `DEFAULT_LIMIT` of 10, because a silent 10 on a page whose whole
 * safety story is "pick a row, never type an id" lets an operator conclude a
 * workspace does not exist when it was simply the eleventh match.
 */
export const WORKSPACE_SEARCH_LIMIT = 25;

export async function searchWorkspaces(term: string): Promise<readonly WorkspaceMatch[]> {
  const query: IdentityQuery<"searchWorkspaces"> = { q: term, limit: WORKSPACE_SEARCH_LIMIT };
  const response = await apiClient.get<WorkspaceSearchResponse>(
    `/identity/admin/workspaces?q=${encodeURIComponent(query.q)}&limit=${String(query.limit)}`
  );
  return response.data;
}

/**
 * The entitlements scope a workspace resolves to.
 *
 * A business Space IS its own enterprise (`spaces.id = enterprises.id`, ENR-929
 * migration 026), so the workspace id is the enterprise scope id. Kept in one
 * function rather than inline at the call sites, because a page that built this
 * ref two ways could read one scope and write another.
 */
export function workspaceScope(
  workspace: WorkspaceMatch
): Extract<ScopeRef, { kind: "enterprise" }> {
  return { kind: "enterprise", id: workspace.id };
}
